import axios from 'axios';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
//import { msalConfig, loginRequest } from '../authConfig';
import config from '../configLoader';
import { _createAlert } from './apiUtils';

const msalConfig = config.msalConfig;
const loginRequest = config.loginRequest;

console.log(msalConfig, loginRequest);

const msalInstance = new PublicClientApplication(msalConfig);

// Initialize the MSAL instance
async function initializeMsal() {
    await msalInstance.initialize();
}

/** The Azure tenant this build signs into, from its authority URL (undefined for /common). */
const configTenantID = (() => {
    const match = /login\.microsoftonline\.com\/([0-9a-f-]{36})/i.exec(msalConfig?.auth?.authority ?? '');
    return match ? match[1].toLowerCase() : undefined;
})();

/**
 * The account to request tokens for: the active account (set on sign-in in
 * useAdminSession), else the cached account in this build's tenant, else the first
 * cached one. getAllAccounts()[0] alone can be a different Microsoft account when more
 * than one is cached in this browser (localhost:8080 is shared by every tenant), and
 * silent token requests for it fail with InteractionRequired on every call. The pick is
 * pinned as the active account so later requests skip the search.
 */
function currentAccount() {
    const active = msalInstance.getActiveAccount();
    if (active) return active;
    const accounts = msalInstance.getAllAccounts();
    const account = accounts.find((a) => configTenantID && a.tenantId?.toLowerCase() === configTenantID) ?? accounts[0];
    if (account) msalInstance.setActiveAccount(account);
    return account;
}

// Create the API client with axios
const apiClient = axios.create({
    baseURL: config.apiUrl,
    //baseURL: 'http://localhost:3000/',
    headers: { accept: 'application/json' }
});

// Add token to the API request header
apiClient.interceptors.request.use(async (config) => {
    try {
        const account = currentAccount();
        if (!account) {
            console.log('No user is signed in.');
            return config;
        }

        try {
            const response = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account: account
            });

            if (response && response.accessToken) {
                config.headers['Authorization'] = `Bearer ${response.accessToken}`;
            }
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                console.log('Interaction required to acquire token.');
                const response = await msalInstance.acquireTokenPopup({
                    ...loginRequest,
                    account: account
                });
                // The user may pick a different account in the popup; pin it, or the next
                // request asks for the old account again and pops up again.
                if (response?.account) msalInstance.setActiveAccount(response.account);

                if (response && response.accessToken) {
                    config.headers['Authorization'] = `Bearer ${response.accessToken}`;
                }
            } else {
                throw error;
            }
        }
    } catch (error) {
        console.error('Error acquiring token:', error);
    }
    return config;
});

/**
 * Employee Time Portal (P-TAS-20): the request interceptor above swallows a failed token
 * acquisition (a blocked popup included) and sends the request with NO bearer, which the
 * API answers 401. A punch must never be sent — or auto-retried — that way, so the portal
 * checks first with getTokenStatus() (silent only, never a popup) and, on 401, asks the
 * user to sign in again through reacquireTokenInteractive(), called from a click handler so
 * the browser allows the popup.
 */
export type TokenStatus = 'ok' | 'expired' | 'unreachable';

/** 'expired' = the user must sign in again; 'unreachable' = the silent refresh failed for
 *  another reason (offline, identity provider down) — retry later like any network error. */
export async function getTokenStatus(): Promise<TokenStatus> {
    try {
        const account = currentAccount();
        if (!account) return 'expired';
        const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
        return response?.accessToken ? 'ok' : 'expired';
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) return 'expired';
        return typeof navigator !== 'undefined' && navigator.onLine === false ? 'unreachable' : 'expired';
    }
}

/** Interactive token re-acquire for the "Your session expired" modal. Resolves true on success. */
export async function reacquireTokenInteractive(): Promise<boolean> {
    try {
        const account = currentAccount();
        const response = await msalInstance.acquireTokenPopup(account ? { ...loginRequest, account } : { ...loginRequest });
        if (response?.account) msalInstance.setActiveAccount(response.account);
        return !!response?.accessToken;
    } catch (error) {
        console.error('Interactive token re-acquire failed:', error);
        return false;
    }
}

/**
 * Central /admin gate rejections (API src/auth/requireAdminAccess.ts, TAS-667):
 * 403 { code: 'ADMIN_ACCESS_REQUIRED' | 'OID_MISMATCH', message } when
 * ADMIN_GATE=enforce. Show the server's message once (a page fires many requests
 * at a time) and pass the rejection on. Never sign the user out here: that would
 * loop straight back into the same 403 on the next sign-in.
 */
const ADMIN_GATE_CODES = new Set(['ADMIN_ACCESS_REQUIRED', 'OID_MISMATCH']);
const ADMIN_GATE_ALERT_COOLDOWN_MS = 30 * 1000;
let lastAdminGateAlertAt = 0;

export function isAdminGateRejection(error: unknown): boolean {
    const response = (error as { response?: { status?: number; data?: { code?: unknown } } })?.response;
    return response?.status === 403 && typeof response.data?.code === 'string' && ADMIN_GATE_CODES.has(response.data.code);
}

apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (isAdminGateRejection(error)) {
            const data = error.response.data as { code: string; message?: string };
            console.warn('Admin App access rejected:', data.code, data.message);
            // Pages that surface error.message show the server's explanation.
            if (data.message) error.message = data.message;
            const now = Date.now();
            if (now - lastAdminGateAlertAt > ADMIN_GATE_ALERT_COOLDOWN_MS && typeof _createAlert === 'function') {
                lastAdminGateAlertAt = now;
                _createAlert(
                    data.code === 'OID_MISMATCH' ? 'Sign-in mismatch' : 'Admin access required',
                    data.message || 'Your account does not have Admin App access.'
                );
            }
        }
        return Promise.reject(error);
    }
);

// Ensure MSAL is initialized before making any requests
initializeMsal().catch(error => {
    console.error('Error initializing MSAL:', error);
});

export default apiClient;